import { useCallback, useEffect, useMemo, useState } from 'react';
import { pdApi, type PdProcessL1, type PdProcessL2, type PdProcessL3 } from '../api';
import './ProcessHierarchy.css';
import { HelpPanel } from '../HelpPanel';
import { PROCESS_HIERARCHY } from '../helpContent';

/**
 * Settings → Business Process Definition → L1–L3 Process Hierarchy.
 *
 * CLONED from `/Users/glucic/src/ai_readiness_template/views/opportunities-process-hierarchy.ejs`.
 * Heading, tab labels, column order and widths, empty-state wording and the Material
 * chrome are the source's.
 *
 * NOTE THE TWO SPELLINGS, both deliberate: the left nav says "L1–L3" with an EN DASH and
 * this page heading says "L1 - L3" with a hyphen. They disagree in the source; normalising
 * either would be a silent edit to copy nobody asked to change.
 *
 * L3 HAS NO DESCRIPTION FIELD. Not an omission — the source hardcodes `l3_description` to
 * NULL on create and never touches it on update, so the column exists but no screen can
 * fill it. Reproduced; logged in docs/DEVIATIONS.md.
 *
 * DELETES ARE HARD DELETES and the server refuses them while children or opportunities
 * exist, returning the reason, which is shown verbatim.
 */

type Tab = 'l1' | 'l2' | 'l3';

interface EditState {
  level: Tab;
  id?: number;
  name: string;
  description: string;
  parentId?: number;
}

const TABS: { key: Tab; label: string }[] = [
  { key: 'l1', label: 'L1 Process Groups' },
  { key: 'l2', label: 'L2 Process Areas' },
  { key: 'l3', label: 'L3 Activities' },
];

export function ProcessHierarchy() {
  const [tab, setTab] = useState<Tab>('l1');
  const [l1, setL1] = useState<PdProcessL1[]>([]);
  const [l2, setL2] = useState<PdProcessL2[]>([]);
  const [l3, setL3] = useState<PdProcessL3[]>([]);
  /*
    THE PAGE HEIGHT IS MEASURED. `.ph-page` is `height: 100%`, but this shell never bounds
    its content area — `<main>` carries `min-height: 100vh` and then grows — so 100% had
    nothing to resolve against and the window scrolled by 1,477px while the table's own
    scroll area sat unused. Same fix, same reason, as Delivery Pathway; CLAUDE.md: measure
    it rather than typing a constant that describes another element's size.

    Callback ref, not `useRef`: the table mounts after the loading gate.
  */
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [filter, setFilter] = useState('');
  const [cascadeL1, setCascadeL1] = useState<number | undefined>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.settings.processHierarchy();
      setL1(data.l1_processes);
      setL2(data.l2_processes);
      setL3(data.l3_processes);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Each tab keeps its own filter — switching tab clears it, as the source does.
  useEffect(() => {
    setFilter('');
  }, [tab]);

  const q = filter.trim().toLowerCase();
  const shownL1 = useMemo(
    () => (q ? l1.filter((r) => `${r.l1_code} ${r.l1_name} ${r.l1_description ?? ''}`.toLowerCase().includes(q)) : l1),
    [l1, q],
  );
  const shownL2 = useMemo(
    () =>
      q
        ? l2.filter((r) =>
            `${r.l2_code} ${r.l2_name} ${r.l1_name ?? ''} ${r.l2_description ?? ''}`.toLowerCase().includes(q),
          )
        : l2,
    [l2, q],
  );
  const shownL3 = useMemo(
    () =>
      q
        ? l3.filter((r) =>
            `${r.l3_code} ${r.l3_name} ${r.l1_name ?? ''} ${r.l2_name ?? ''}`.toLowerCase().includes(q),
          )
        : l3,
    [l3, q],
  );

  const cascadedL2 = useMemo(
    () => (cascadeL1 ? l2.filter((x) => x.l1_id === cascadeL1) : l2),
    [l2, cascadeL1],
  );

  const run = async (fn: () => Promise<{ message?: string }>) => {
    setError(null);
    setNotice(null);
    try {
      const r = await fn();
      if (r.message) setNotice(r.message);
      setEdit(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const save = () => {
    if (!edit) return;
    const { level, id, name, description, parentId } = edit;
    if (level === 'l1') {
      return run(() =>
        id
          ? pdApi.settings.updateL1(id, { l1_name: name, l1_description: description })
          : pdApi.settings.createL1({ l1_name: name, l1_description: description }),
      );
    }
    if (level === 'l2') {
      return run(() =>
        id
          ? pdApi.settings.updateL2(id, { l2_name: name, l2_description: description, l1_id: parentId })
          : pdApi.settings.createL2({ l1_id: parentId, l2_name: name, l2_description: description }),
      );
    }
    return run(() =>
      id
        ? pdApi.settings.updateL3(id, { l3_name: name, l2_id: parentId })
        : pdApi.settings.createL3({ l2_id: parentId, l3_name: name }),
    );
  };

  const remove = (level: Tab, id: number, label: string) => {
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    return run(() =>
      level === 'l1'
        ? pdApi.settings.deleteL1(id)
        : level === 'l2'
          ? pdApi.settings.deleteL2(id)
          : pdApi.settings.deleteL3(id),
    );
  };

  const total = tab === 'l1' ? l1.length : tab === 'l2' ? l2.length : l3.length;
  const shownCount = tab === 'l1' ? shownL1.length : tab === 'l2' ? shownL2.length : shownL3.length;

  const addLabel =
    tab === 'l1' ? 'Add L1 Process' : tab === 'l2' ? 'Add L2 Process' : 'Add L3 Activity';
  const filterLabel =
    tab === 'l1' ? 'L1 Process:' : tab === 'l2' ? 'L2 Process:' : 'L3 Activity:';

  /** Header and body are two tables, so the header can stay put; widths must match. */
  const columns =
    tab === 'l1'
      ? [
          { label: '#', width: 80 },
          { label: 'Name', width: 250 },
          { label: 'Description', width: 400 },
          { label: 'L2 Count', width: 120 },
          { label: 'Actions', width: 150 },
        ]
      : tab === 'l2'
        ? [
            { label: '#', width: 80 },
            { label: 'L1 Process', width: 200 },
            { label: 'L2 Name', width: 250 },
            { label: 'Description', width: 350 },
            { label: 'L3 Count', width: 120 },
            { label: 'Actions', width: 150 },
          ]
        : [
            { label: '#', width: 80 },
            { label: 'L1 Process', width: 200 },
            { label: 'L2 Process', width: 250 },
            { label: 'L3 Activity Name', width: 400 },
            { label: 'Actions', width: 150 },
          ];

  const colGroup = (
    <colgroup>
      {columns.map((c) => (
        <col key={c.label} style={{ width: c.width }} />
      ))}
    </colgroup>
  );

  const emptyTitle =
    tab === 'l1'
      ? 'No L1 Processes Found'
      : tab === 'l2'
        ? 'No L2 Processes Found'
        : 'No L3 Activities Found';

  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  return (
    <div className="ph-page" ref={setRootEl} style={rootH ? { height: rootH } : undefined}>
      <div className="ph-fixed">
        <div className="ph-page-header">
          <div>
            <h1 className="ph-page-header__title">L1 - L3 Process Hierarchy</h1>
            <p className="ph-page-header__subtitle">Process Hierarchy (L1 → L2 → L3)</p>
          </div>
          {/*
            The Add button sits UNDER "How this works" (owner, 2026-08-09), not down beside
            the filter. Its label and target follow the active tab, so one button serves all
            three levels — L1, L2 and L3 — exactly as it did lower down.
          */}
          <div className="pd-page-header__actions ph-header-actions">
            <HelpPanel help={PROCESS_HIERARCHY} />
            <button
              type="button"
              className="ph-add-btn"
              onClick={() =>
                setEdit({
                  level: tab,
                  name: '',
                  description: '',
                  ...(tab === 'l2' ? { parentId: l1[0]?.l1_id } : {}),
                  ...(tab === 'l3' ? { parentId: undefined } : {}),
                })
              }
            >
              + {addLabel}
            </button>
          </div>
        </div>

        <div className="ph-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`ph-tab-button${tab === t.key ? ' active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error ? <div className="ph-error">{error}</div> : null}
      {notice ? <div className="ph-notice">{notice}</div> : null}

      <div className="ph-tab-content">
        <div className="ph-actions-bar">
          {total > 0 ? (
            <div className="md-card ph-filter-card">
              <div className="ph-filter-row">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                  <label className="ph-filter-label" htmlFor="ph-filter">
                    {filterLabel}
                  </label>
                  <input
                    id="ph-filter"
                    className="ph-filter-input"
                    type="text"
                    placeholder="Type to filter..."
                    autoComplete="off"
                    spellCheck={false}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                </div>
                {filter ? (
                  <button type="button" className="ph-clear-btn" onClick={() => setFilter('')}>
                    Clear
                  </button>
                ) : null}
                <span className="ph-record-count">
                  {filter ? `${shownCount} of ${total} records` : `${total} total records`}
                </span>
              </div>
            </div>
          ) : null}
        </div>

        <div className="ph-scroll-area">
          <div className="md-card ph-table-card">
            {loading ? (
              <div className="md-empty-state">
                <p>Loading…</p>
              </div>
            ) : total === 0 ? (
              <div className="md-empty-state" style={{ padding: 40 }}>
                <div className="md-empty-icon">📐</div>
                <h3>{emptyTitle}</h3>
                <p>Use the action cards above to add {tab === 'l3' ? 'L3 activities' : `${tab.toUpperCase()} processes`}</p>
              </div>
            ) : (
              <>
                <div className="ph-table-head-wrap">
                  <table className="md-table">
                    {colGroup}
                    <thead>
                      <tr>
                        {columns.map((c) => (
                          <th key={c.label}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                  </table>
                </div>

                <div className="ph-table-body-wrap">
                  <table className="md-table">
                    {colGroup}
                    <tbody>
                      {tab === 'l1'
                        ? shownL1.map((r) => (
                            <tr key={r.l1_id}>
                              <td>{r.l1_code}</td>
                              <td>{r.l1_name}</td>
                              <td title={r.l1_description ?? ''}>{r.l1_description}</td>
                              <td>{r.l2_count ?? 0}</td>
                              <td>
                                <div className="ph-row-actions">
                                  <button
                                    type="button"
                                    className="md-icon-button"
                                    title="View"
                                    onClick={() => {
                                      setCascadeL1(r.l1_id);
                                      setTab('l2');
                                    }}
                                  >
                                    👁️
                                  </button>
                                  <button
                                    type="button"
                                    className="md-icon-button"
                                    title="Edit"
                                    onClick={() =>
                                      setEdit({
                                        level: 'l1',
                                        id: r.l1_id,
                                        name: r.l1_name,
                                        description: r.l1_description ?? '',
                                      })
                                    }
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    type="button"
                                    className="md-icon-button"
                                    title="Delete"
                                    onClick={() => remove('l1', r.l1_id, r.l1_name)}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        : tab === 'l2'
                          ? shownL2.map((r) => (
                              <tr key={r.l2_id}>
                                <td>{r.l2_code}</td>
                                <td>{r.l1_name}</td>
                                <td>{r.l2_name}</td>
                                <td title={r.l2_description ?? ''}>{r.l2_description}</td>
                                <td>{r.l3_count ?? 0}</td>
                                <td>
                                  <div className="ph-row-actions">
                                    <button
                                      type="button"
                                      className="md-icon-button"
                                      title="View"
                                      onClick={() => {
                                        setCascadeL1(r.l1_id);
                                        setTab('l3');
                                      }}
                                    >
                                      👁️
                                    </button>
                                    <button
                                      type="button"
                                      className="md-icon-button"
                                      title="Edit"
                                      onClick={() =>
                                        setEdit({
                                          level: 'l2',
                                          id: r.l2_id,
                                          name: r.l2_name,
                                          description: r.l2_description ?? '',
                                          parentId: r.l1_id,
                                        })
                                      }
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      type="button"
                                      className="md-icon-button"
                                      title="Delete"
                                      onClick={() => remove('l2', r.l2_id, r.l2_name)}
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          : shownL3.map((r) => (
                              <tr key={r.l3_id}>
                                <td>{r.l3_code}</td>
                                <td>{r.l1_name}</td>
                                <td>{r.l2_name}</td>
                                <td>{r.l3_name}</td>
                                <td>
                                  <div className="ph-row-actions">
                                    <button
                                      type="button"
                                      className="md-icon-button"
                                      title="Edit"
                                      onClick={() =>
                                        setEdit({
                                          level: 'l3',
                                          id: r.l3_id,
                                          name: r.l3_name,
                                          description: '',
                                          parentId: r.l2_id,
                                        })
                                      }
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      type="button"
                                      className="md-icon-button"
                                      title="Delete"
                                      onClick={() => remove('l3', r.l3_id, r.l3_name)}
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {edit ? (
        <div className="ph-modal-backdrop" onClick={() => setEdit(null)}>
          <div className="ph-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ph-modal-head">
              <h2>
                {edit.id ? 'Edit ' : 'Create New '}
                {edit.level === 'l1' ? 'L1 Process' : edit.level === 'l2' ? 'L2 Process' : 'L3 Activity'}
              </h2>
              <button type="button" className="ph-modal-close" onClick={() => setEdit(null)}>
                ×
              </button>
            </div>

            <div className="ph-modal-body">
              {edit.level === 'l2' ? (
                <div className="ph-field">
                  <label htmlFor="ph-parent-l1">L1 Process *</label>
                  <select
                    id="ph-parent-l1"
                    value={edit.parentId ?? ''}
                    onChange={(e) => setEdit({ ...edit, parentId: Number(e.target.value) })}
                  >
                    <option value="">-- Choose L1 Process --</option>
                    {l1.map((o) => (
                      <option key={o.l1_id} value={o.l1_id}>
                        {o.l1_name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {edit.level === 'l3' ? (
                <>
                  <div className="ph-field">
                    <label htmlFor="ph-cascade-l1">L1 Process</label>
                    <select
                      id="ph-cascade-l1"
                      value={cascadeL1 ?? ''}
                      onChange={(e) => {
                        setCascadeL1(e.target.value ? Number(e.target.value) : undefined);
                        setEdit({ ...edit, parentId: undefined });
                      }}
                    >
                      <option value="">-- Choose L1 Process --</option>
                      {l1.map((o) => (
                        <option key={o.l1_id} value={o.l1_id}>
                          {o.l1_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ph-field">
                    <label htmlFor="ph-parent-l2">L2 Process *</label>
                    <select
                      id="ph-parent-l2"
                      value={edit.parentId ?? ''}
                      onChange={(e) => setEdit({ ...edit, parentId: Number(e.target.value) })}
                    >
                      <option value="">-- Choose L2 Process --</option>
                      {cascadedL2.map((o) => (
                        <option key={o.l2_id} value={o.l2_id}>
                          {o.l2_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : null}

              <div className="ph-field">
                <label htmlFor="ph-name">
                  {edit.level === 'l3' ? 'L3 Activity Name *' : 'Name *'}
                </label>
                <input
                  id="ph-name"
                  type="text"
                  maxLength={255}
                  value={edit.name}
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                />
              </div>

              {/* No description on L3 — see the note at the head of this file. */}
              {edit.level !== 'l3' ? (
                <div className="ph-field">
                  <label htmlFor="ph-desc">Description</label>
                  <textarea
                    id="ph-desc"
                    value={edit.description}
                    onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                  />
                </div>
              ) : null}
            </div>

            <div className="ph-modal-foot">
              <button type="button" className="ph-modal-cancel" onClick={() => setEdit(null)}>
                Cancel
              </button>
              <button type="button" className="ph-modal-save" onClick={save}>
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
